<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxBanner,
  NxButton,
  NxErrorSummary,
  NxLabel,
  NxLoadingWrapper,
  NxSidePanel,
  NxTagInput,
  NxTextInput,
  NxToggleGroup,
} from '@blpde/nx-ui';
import { useSearchDictionaryClient } from '../../search/dictionaryApi';
import type { DictionaryLanguage } from '../dictionaryFile';
import { dictionaryFold } from '../dictionaryFold';
import { dictionaryWriteFailure, type DictionaryWriteResult } from './level';
import { DICTIONARY_TEXT } from './labels';
import type { DraftProblem, SynonymDraft } from './drafts';
import { useDictionaryEditorWrite } from './editorSave';
import ExplainPanel from './ExplainPanel.vue';

/**
 * The side panel of one synonym group (I-3024): name, direction, the source
 * of a one-way group, the terms and the language, with OpenThesaurus
 * proposals for the term being typed. Saving checks the draft first (the
 * word rule, 2–50 terms, a twin name, a term another group of the layer
 * holds — which offers to open that group); master's own refusal shows in the
 * panel's error summary, the draft left as typed.
 */
const props = defineProps<{
  /** The group being edited, or nothing for a new one. */
  editing: { lang: DictionaryLanguage; name: string } | null;
  name: string;
  lang: DictionaryLanguage;
  oneWay: boolean;
  /** The source of a one-way group. */
  source: string;
  terms: string[];
  /** Whose layer is written, as the subtitle names it. */
  levelLabel: string;
  problem: (draft: SynonymDraft) => DraftProblem | null;
  save: (draft: SynonymDraft) => Promise<DictionaryWriteResult>;
}>();

const emit = defineEmits<{ close: []; reload: []; openGroup: [name: string] }>();

const { t } = useI18n();
const client = useSearchDictionaryClient();
const T = `${DICTIONARY_TEXT}.editor`;
const name = ref(props.name);
const direction = ref<'both' | 'one'>(props.oneWay ? 'one' : 'both');
const oneWay = computed(() => direction.value === 'one');
const source = ref(props.source);
const terms = ref([...props.terms]);
const lang = ref<DictionaryLanguage>(props.lang);
const conflict = ref<DraftProblem | null>(null);
/**
 * What the check beside the form asks about: the check explains one term, so
 * it is the source of a one-way group, else the group's first term.
 */
const initialQuery = (props.oneWay ? props.source.trim() : '') || props.terms[0]?.trim() || '';
const proposals = ref<string[]>([]);
const errorSummary = ref<{ focus: () => void } | null>(null);
const fieldLabel: Record<string, string> = {
  name: t(`${T}.name`),
  terms: t(`${T}.terms`),
  source: t(`${T}.source`),
  lang: t(`${T}.language`),
};
const { entries: summaryEntries, onReload, onCopyFailed, focusRequests, fieldErrors, locked, saving, run } =
  useDictionaryEditorWrite({
    kind: () => (props.editing ? 'update' : 'create'),
    fields: ['name', 'source', 'terms', 'lang'],
    labelFor: (field) => (field.key ? fieldLabel[field.key] : undefined),
    reload: () => emit('reload'),
  });

watch(name, () => fieldErrors.clear('name'));
watch(source, () => fieldErrors.clear('source'));
watch(terms, () => {
  fieldErrors.clear('terms');
  conflict.value = null;
});
watch(lang, () => {
  fieldErrors.clear('lang');
  conflict.value = null;
});
watch(focusRequests, () => errorSummary.value?.focus());

function requestClose(): void {
  if (locked.value) return;
  emit('close');
}

function submit(): void {
  if (locked.value) return;
  const draft: SynonymDraft = {
    name: name.value,
    lang: lang.value,
    oneWay: oneWay.value,
    source: source.value,
    terms: terms.value,
  };
  const problem = props.problem(draft);
  conflict.value = null;
  if (problem) {
    fieldErrors.show(new Map([[problem.field, t(`${T}.problems.${problem.code}`, problem.params)]]));
    if (problem.group) conflict.value = problem;
    return;
  }
  void run(async () => {
    const answer = await props.save(draft);
    if (!answer.ok) throw dictionaryWriteFailure(answer);
    emit('close');
  });
}

let thesaurusTimer: ReturnType<typeof setTimeout> | null = null;
let thesaurusGeneration = 0;

function proposalKey(value: string): string {
  return dictionaryFold(value);
}

function proposalsFrom(groups: string[][], query: string): string[] {
  const seen = new Set<string>([query, source.value, ...terms.value].map(proposalKey));
  const out: string[] = [];
  for (const group of groups) {
    for (const term of group) {
      const key = proposalKey(term);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(term);
    }
  }
  return out;
}

function onTermDraft(event: Event): void {
  const value = event.target instanceof HTMLInputElement ? event.target.value : '';
  if (thesaurusTimer) clearTimeout(thesaurusTimer);
  const generation = ++thesaurusGeneration;
  const query = value.trim();
  if (!query) {
    proposals.value = [];
    return;
  }
  thesaurusTimer = setTimeout(() => {
    thesaurusTimer = null;
    void loadProposals(query, generation);
  }, 300);
}

async function loadProposals(query: string, generation: number): Promise<void> {
  try {
    const body = await client.thesaurus(query);
    if (generation !== thesaurusGeneration) return;
    proposals.value = proposalsFrom(body.groups ?? [], query);
  } catch {
    if (generation !== thesaurusGeneration) return;
    proposals.value = [];
  }
}

function addProposal(term: string): void {
  const key = proposalKey(term);
  if (!key || terms.value.some((item) => proposalKey(item) === key)) return;
  terms.value = [...terms.value, term];
  proposals.value = proposals.value.filter((item) => proposalKey(item) !== key);
}

onUnmounted(() => {
  thesaurusGeneration += 1;
  if (thesaurusTimer) clearTimeout(thesaurusTimer);
});

const directions = [
  { value: 'both' as const, label: t(`${DICTIONARY_TEXT}.bothWays`) },
  { value: 'one' as const, label: t(`${DICTIONARY_TEXT}.oneWay`) },
];
const languages = [
  { value: 'de' as const, label: t(`${DICTIONARY_TEXT}.langs.de`) },
  { value: 'en' as const, label: t(`${DICTIONARY_TEXT}.langs.en`) },
];
</script>

<template>
  <NxSidePanel
    :title="t(editing ? `${T}.groupTitle` : `${T}.groupCreateTitle`)"
    :subtitle="levelLabel"
    width="lg"
    :close-label="t(`${DICTIONARY_TEXT}.close`)"
    @close="requestClose"
  >
    <div class="dict-side">
      <NxErrorSummary
        ref="errorSummary"
        data-testid="dictionary-editor-error"
        :entries="summaryEntries"
        @reload="onReload"
        @copy-failed="onCopyFailed"
      />
      <NxLabel :text="t(`${T}.name`)">
        <NxLoadingWrapper
          data-testid="dictionary-editor-name"
          :loading="false"
          :error="!!fieldErrors.errorFor('name')"
          :error-message="fieldErrors.errorFor('name')"
        >
          <NxTextInput v-model="name" :disabled="locked" />
        </NxLoadingWrapper>
      </NxLabel>
      <NxLabel :text="t(`${T}.direction`)">
        <NxToggleGroup v-model="direction" :label="t(`${T}.direction`)" :options="directions" :disabled="locked" />
      </NxLabel>
      <NxLabel v-if="oneWay" :text="t(`${T}.source`)">
        <NxLoadingWrapper
          data-testid="dictionary-editor-source"
          :loading="false"
          :error="!!fieldErrors.errorFor('source')"
          :error-message="fieldErrors.errorFor('source')"
        >
          <NxTextInput v-model="source" :disabled="locked" />
        </NxLoadingWrapper>
      </NxLabel>
      <NxLabel :text="t(`${T}.language`)">
        <NxToggleGroup v-model="lang" :label="t(`${DICTIONARY_TEXT}.langs.label`)" :options="languages" :disabled="locked" />
      </NxLabel>
      <NxLabel :text="oneWay ? t(`${T}.foundTerms`) : t(`${T}.terms`)">
        <NxLoadingWrapper
          data-testid="dictionary-editor-terms"
          :loading="false"
          :error="!!fieldErrors.errorFor('terms')"
          :error-message="fieldErrors.errorFor('terms')"
        >
          <NxTagInput v-model="terms" :disabled="locked" :placeholder-more="t(`${T}.addTerm`)" @input="onTermDraft" />
        </NxLoadingWrapper>
      </NxLabel>
      <NxBanner
        v-if="proposals.length"
        quiet
        variant="info"
        :title="t(`${T}.fromOpenThesaurus`)"
        wrap-buttons
        data-testid="dictionary-thesaurus"
      >
        <template #buttons>
          <NxButton v-for="proposal in proposals" :key="proposal" variant="link" @click="addProposal(proposal)">
            {{ proposal }}
          </NxButton>
        </template>
      </NxBanner>
      <NxBanner
        v-if="conflict?.group"
        variant="warning"
        wrap-buttons
        :title="t(`${T}.problems.term_in_two_groups`, conflict.params)"
        data-testid="dictionary-conflict"
      >
        {{ t(`${T}.openThereQuestion`) }}
        <template #buttons>
          <NxButton variant="outline" @click="emit('openGroup', conflict.group!)">
            {{ t(`${T}.openThere`) }}
          </NxButton>
        </template>
      </NxBanner>
      <ExplainPanel :initial-query="initialQuery" />
    </div>
    <template #footer>
      <div class="dict-side-foot">
        <NxButton variant="outline" :disabled="locked" @click="requestClose">{{ t(`${T}.cancel`) }}</NxButton>
        <NxButton data-testid="dictionary-save-group" :disabled="locked" :loading="saving" @click="submit">
          {{ t(`${T}.save`) }}
        </NxButton>
      </div>
    </template>
  </NxSidePanel>
</template>

<style scoped>
.dict-side {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
}
.dict-side-foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
}
</style>
